import { useQuery, type QueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api-client";

/** The signed-in user, exactly as GET /api/v1/auth/session (and /me) return it. */
export type SessionUser = {
  id: string;
  email: string;
  role: string;
  displayName: string | null;
  uiLocale: string;
  avatarUrl: string | null;
  createdAt: string;
  gemBalance: number;
  hasPassword: boolean;
  subscription: { planName: string; expiresAt: string } | null;
};

export const sessionQueryKey = ["session"] as const;

/** GET /api/v1/auth/session answers 200 either way — `user: null` when signed out. */
async function fetchSession(): Promise<SessionUser | null> {
  return (await apiRequest<{ user: SessionUser | null }>("/api/v1/auth/session")).user;
}

/**
 * "Who is signed in" for the whole app — one cached query (backed by
 * GET /api/v1/auth/session) that every guard, header and page shares.
 * `data` is `null` when signed out and `undefined` while the first check
 * is still loading. Use refreshSession() after anything that signs someone
 * in, and useLogout() to sign out.
 */
export function useSession() {
  return useQuery({ queryKey: sessionQueryKey, queryFn: fetchSession, staleTime: 5 * 60_000 });
}

/** The name the UI greets someone by: their display name, else their email. */
export function sessionDisplayName(user: Pick<SessionUser, "displayName" | "email">): string {
  return user.displayName ?? user.email;
}

/**
 * Re-reads the session from the server and waits for the answer — call
 * after anything that signs someone in (login, email verification,
 * password reset) BEFORE navigating into a protected area.
 */
export function refreshSession(queryClient: QueryClient): Promise<SessionUser | null> {
  return queryClient.fetchQuery({ queryKey: sessionQueryKey, queryFn: fetchSession, staleTime: 0 });
}
